import { useMemo } from 'react';
import {
  FilterDrawerDisplayResult,
  FilterDrawerFilter,
  FilterDrawerProcessingOptions,
  IFilterDrawerSchema,
  IOption
} from '../types';
import { getOptionsAndLabels } from '../utils';
import { _map } from '@cp/src/utils/commonUtils';
import { DATE_QUERY_PARAMS, FilterKeys } from '../constants';
import { APPROX_CHAR_WIDTH, FILTERS_PADDING } from '@cp/src/constants/Global';

type NestedFilterValue = Record<string, string[]> | undefined;
type SimpleFilterValue = string[] | undefined;

interface FilterProcessingResult {
  count: number;
  tooltipLabel: string;
  showFilter: boolean;
}

const normalizeActiveFilters = (activeFilters: SimpleFilterValue): string[] => {
  if (!activeFilters) return [];
  return Array.isArray(activeFilters) ? activeFilters : [];
};

const findSelectedOptions = (options: IOption[], activeFilters: SimpleFilterValue): IOption[] => {
  if (!options || !Array.isArray(options)) return [];

  const normalizedFilters = normalizeActiveFilters(activeFilters);
  return options.filter(option => normalizedFilters.includes(option.value));
};

const generateTooltipLabel = (
  selectedOptions: IOption[],
  activeFilters: SimpleFilterValue
): string => {
  if (selectedOptions.length > 0) {
    return selectedOptions.map(option => option?.label).join(', ');
  }
  const normalizedFilters = normalizeActiveFilters(activeFilters);
  return normalizedFilters.join(', ');
};

const shouldShowFilter = (activeFilters: SimpleFilterValue): boolean => {
  if (!activeFilters) return false;
  return Array.isArray(activeFilters) ? activeFilters.length > 0 : true;
};

const processNestedFilter = (
  activeFilters: NestedFilterValue,
  options: IOption[],
  filterSchema: Record<string, IFilterDrawerSchema>,
  filterKey: string
): FilterProcessingResult => {
  if (!activeFilters) {
    return {
      count: 0,
      tooltipLabel: '',
      showFilter: false
    };
  }

  const { count: activeNestedFiltersCount, tooltipLabel } = Object.keys(activeFilters).reduce(
    (acc, nestedFilterKey) => {
      const nestedValue = activeFilters?.[nestedFilterKey]?.[0];
      const nestedValueText = options?.find((opt: IOption) => opt.value === nestedValue)?.label;
      const nestedLabel = filterSchema?.[filterKey]?.options?.find(
        (opt: IOption) => (opt as any).filterSectionKey === nestedFilterKey
      )?.label;

      if (nestedValue && nestedValueText && nestedLabel) {
        acc.count += 1;
        acc.tooltipLabel += `${nestedLabel}: ${nestedValueText}\n`;
      }

      return acc;
    },
    { count: 0, tooltipLabel: '' }
  );

  return {
    count: activeNestedFiltersCount,
    tooltipLabel: tooltipLabel.trim(),
    showFilter: activeNestedFiltersCount > 0
  };
};

const processRegularFilter = (
  activeFilters: SimpleFilterValue,
  options: IOption[],
  filterSchema: Record<string, IFilterDrawerSchema>,
  filterKey: string
): FilterProcessingResult => {
  const availableOptions = options || filterSchema?.[filterKey]?.options || [];
  const selectedOptions = findSelectedOptions(availableOptions, activeFilters);
  const tooltipLabel = generateTooltipLabel(selectedOptions, activeFilters);
  const filterCount = normalizeActiveFilters(activeFilters).length;
  const showFilter = shouldShowFilter(activeFilters);

  return {
    count: filterCount,
    tooltipLabel,
    showFilter
  };
};

export const useFilterDrawerLogic = (
  options: FilterDrawerProcessingOptions
): FilterDrawerDisplayResult => {
  const { filterSchema, queryParams, filterSchemaMap, maxWidth = 1060 } = options;

  return useMemo(() => {
    let activeFiltersCount = 0;
    let totalWidth = 0;
    const displayedFilters: FilterDrawerFilter[] = [];
    const hiddenFilters: FilterDrawerFilter[] = [];

    for (const filterKey of filterSchemaMap) {
      if (DATE_QUERY_PARAMS.includes(filterKey as FilterKeys)) continue;

      const { options, label } = getOptionsAndLabels(filterSchema, filterKey);
      const activeFilters = queryParams?.[filterKey];
      const isNested = filterSchema?.[filterKey]?.nestedFilter;

      const filterResult = isNested
        ? processNestedFilter(activeFilters as NestedFilterValue, options, filterSchema, filterKey)
        : processRegularFilter(
            activeFilters as SimpleFilterValue,
            options,
            filterSchema,
            filterKey
          );

      if (filterResult.showFilter) {
        activeFiltersCount += 1;

        const filter: FilterDrawerFilter = {
          filterKey,
          label,
          count: filterResult.count,
          tooltipLabel: filterResult.tooltipLabel
        };

        // Calculate width and categorize in same iteration
        const filterWidth = label.length * APPROX_CHAR_WIDTH + FILTERS_PADDING;
        if (totalWidth + filterWidth <= maxWidth) {
          displayedFilters.push(filter);
          totalWidth += filterWidth;
        } else {
          hiddenFilters.push(filter);
        }
      }
    }

    return {
      displayedFilters,
      hiddenFilters,
      activeFiltersCount,
      totalWidth,
      isEmpty: displayedFilters.length === 0 && hiddenFilters.length === 0
    };
  }, [filterSchema, queryParams, filterSchemaMap, maxWidth]);
};
